import { useCallback, useState } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { useRequestState } from './useRequestState';

export interface QueryResultResponse {
    status: string;
    taskId?: string;
    queryId?: number;
    count?: number;
    personIds?: number[];
    listCount?: Array<{ channel: string; count: number }>;
    message?: string;
    description?: string;
}

export type QueryResultStatus = 'expired' | 'error' | 'completed' | 'pending' | 'unknown';

export const getQueryResultStatus = (result: QueryResultResponse | null): QueryResultStatus => {
    const status = result?.status?.toUpperCase() ?? '';
    if (status.includes('EXPIRED') || status.includes('UNKNOWN')) return 'expired';
    if (status === 'ERROR') return 'error';
    if (status === 'COMPLETED' || status === 'DONE') return 'completed';
    if (status === 'PENDING' || status === 'RUNNING' || status === 'IN_PROGRESS') return 'pending';
    return 'unknown';
};

export const getQueryResultTotal = (result: QueryResultResponse | null): number | null =>
    result?.count ?? result?.listCount?.reduce((acc, item) => acc + item.count, 0) ?? null;

const UNAVAILABLE: QueryResultResponse = { status: 'UNKNOWN OR EXPIRED' };

const isBadRequest = (err: unknown): boolean => {
    const message = err instanceof Error ? err.message.toLowerCase() : '';
    return message.includes('http 400') || message.includes('bad request');
};

interface FetchQueryResultOptions {
    keepPreviousResult?: boolean;
}

export const useQueryResult = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const { loading, error, setError, run } = useRequestState();
    const [result, setResult] = useState<QueryResultResponse | null>(null);

    const fetchQueryResult = useCallback(async (
        queryId: string | number,
        { keepPreviousResult = false }: FetchQueryResultOptions = {}
    ): Promise<QueryResultResponse | null> => {
        if (!isReady || !queryId) return null;
        if (!keepPreviousResult) setResult(null);

        const response = await run(async () => {
            try {
                const raw: QueryResultResponse | null = await authFetch(`/getQueryResult?queryId=${queryId}`, { method: 'GET' });
                return raw ?? UNAVAILABLE;
            } catch (err) {
                if (isBadRequest(err)) return UNAVAILABLE;
                throw err;
            }
        }, 'Erreur lors de la récupération du résultat');

        if (response) setResult(response);
        return response;
    }, [authFetch, isReady, run]);

    const markAsRunning = useCallback((taskId?: string) => {
        setError(null);
        setResult({ status: 'IN_PROGRESS', ...(taskId && { taskId }) });
    }, [setError]);

    const reset = useCallback(() => {
        setResult(null);
        setError(null);
    }, [setError]);

    return { fetchQueryResult, loading, error, result, reset, markAsRunning };
};
